import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import type { RootState } from '../store';
import { env } from '../../utils/env';
import { setCredentials, logout } from '../slices';
import { refreshSession } from '../../services/cognito';

const REQUEST_TIMEOUT_MS = 60_000;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env.API_ENDPOINT,
  timeout: REQUEST_TIMEOUT_MS,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (env.API_KEY) {
      headers.set('x-api-key', env.API_KEY);
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const refreshResult = await refreshSession();

    if (refreshResult) {
      api.dispatch(
        setCredentials({
          user: {
            id: refreshResult.user.userId,
            email: refreshResult.user.email,
            name: refreshResult.user.name,
            organizationId: refreshResult.user.organizationId,
          },
          token: refreshResult.tokens.idToken,
          accessToken: refreshResult.tokens.accessToken,
          refreshToken: refreshResult.tokens.refreshToken,
        })
      );

      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(logout());
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Agent',
    'Organisation',
    'Member',
    'Invitation',
    'Role',
    'Partner',
    'DataSource',
    'Analytics',
    'Profile',
    'Billing',
    'Invoice',
    'Session',
  ],
  endpoints: () => ({}),
});
